import React from "react";
import { LoadingIcon } from "utils/constant";

interface ConfirmationModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  type?: "success" | "warning" | "info" | "danger";
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  isLoading,
  onCancel,
  onConfirm,
  title = "Confirm Submission",
  message = "Please review all entered information carefully. Once confirmed, this action will be submitted and cannot be modified.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "success",
}) => {
  if (!isOpen) return null;

  const typeStyles = {
    success: {
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
      borderColor: "border-green-100",
      buttonBg: "bg-gradient-to-r from-green-600 to-emerald-600",
      buttonHover: "hover:from-green-700 hover:to-emerald-700",
      ringColor: "ring-green-500/20",
    },
    warning: {
      iconBg: "bg-yellow-50",
      iconColor: "text-yellow-600",
      borderColor: "border-yellow-100",
      buttonBg: "bg-gradient-to-r from-yellow-600 to-amber-600",
      buttonHover: "hover:from-yellow-700 hover:to-amber-700",
      ringColor: "ring-yellow-500/20",
    },
    danger: {
      iconBg: "bg-red-50",
      iconColor: "text-red-600",
      borderColor: "border-red-100",
      buttonBg: "bg-gradient-to-r from-red-600 to-rose-600",
      buttonHover: "hover:from-red-700 hover:to-rose-700",
      ringColor: "ring-red-500/20",
    },
    info: {
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      borderColor: "border-blue-100",
      buttonBg: "bg-gradient-to-r from-blue-600 to-indigo-600",
      buttonHover: "hover:from-blue-700 hover:to-indigo-700",
      ringColor: "ring-blue-500/20",
    },
  };

  const styles = typeStyles[type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={onCancel}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 animate-fade-in">
        {/* Header with gradient border */}
        <div className={`border-b ${styles.borderColor} p-6`}>
          <div className="flex flex-col items-center">
            {/* Icon with subtle shadow */}
            <div className={`mb-4 w-16 h-16 ${styles.iconBg} rounded-full flex items-center justify-center shadow-lg ${styles.ringColor} ring-4`}>
              {type === "success" && (
                <div className={`text-3xl ${styles.iconColor} font-bold`}>✓</div>
              )}
              {type === "warning" && (
                <div className={`text-3xl ${styles.iconColor} font-bold`}>!</div>
              )}
              {type === "danger" && (
                <div className={`text-3xl ${styles.iconColor} font-bold`}>✕</div>
              )}
              {type === "info" && (
                <div className={`text-3xl ${styles.iconColor} font-bold`}>i</div>
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
            <p className="text-gray-600 text-center leading-relaxed">{message}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-6 py-3 text-base font-medium border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`flex-1 px-6 py-3 text-base font-medium text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${styles.buttonBg} ${styles.buttonHover} shadow-lg hover:shadow-xl`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5">
                    {LoadingIcon}
                  </div>
                  <span>Submitting...</span>
                </div>
              ) : (
                confirmText
              )}
            </button>
          </div>
          
          {/* Additional info text */}
          <p className="text-xs text-gray-500 text-center mt-4">
            This action cannot be undone. All changes will be permanent.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
